import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import CoinTransactionsTable from "src/components/transactions-table/CoinTransactionsTable";
import TransactionsFilters from "src/components/transactions/ TransactionsFilters";
import { get } from "src/utils/networkUtils";
import useWithUrlTenant from "src/hooks/useTenant";
import CoinTransactionsStatsChart from "src/components/coin-transactions/CoinTransactionsStatsChart";

const TRANSACTION_TYPES = [
  { id: [9, 17], label: "Store" },
  { id: [15, 28], label: "Training" },
  { id: [1], label: "Daily Login" },
  { id: [19, 20], label: "Minigames" },
  { id: [27], label: "Leaderboard" },
  { id: [5, 6, 7], label: "Marketplace" },
];

export default function CoinTransactionsPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const limit = 10;
  const withTenant = useWithUrlTenant();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsDays, setStatsDays] = useState(7);

  useEffect(() => {
    setOffset(0);
    setRows([]);
    setHasMore(true);
  }, [searchQuery, selectedTypes]);

  useEffect(() => {
    setLoading(true);
    const fetchData = setTimeout(() => {
      let url = withTenant(
        `/admin/coins/transactions?offset=${offset}&limit=${limit}`
      );

      if (searchQuery) {
        url += `&query=${encodeURIComponent(searchQuery)}`;
      }
      if (selectedTypes.length > 0) {
        const idsToSend = selectedTypes.flat();
        url += `&filter[type]=${idsToSend.join(",")}`;
      }
      get(url).then((response) => {
        setRows((prev) =>
          offset === 0
            ? response.transactions
            : [...prev, ...response.transactions]
        );

        setHasMore(response.transactions.length === limit);
        setLoading(false);
      });
    }, 500);

    return () => clearTimeout(fetchData);
  }, [searchQuery, selectedTypes, offset, withTenant]);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    get(withTenant(`/admin/coins/transactions/stats?days=${statsDays}`))
      .then((response) => {
        if (cancelled) return;
        setStats(response.stats);
      })
      .catch((error) => {
        console.error("Error fetching coin transaction stats:", error);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statsDays, withTenant]);

  const lastTransactionRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setOffset((prev) => prev + limit);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const filteredRows = rows.filter((row) => {
    const query = searchQuery.trim().toLowerCase();
    const transactionIdMatches = row.transactionId.toString().includes(query);
    const userIdMatches = row.userId.toString().includes(query);
    const referenceMatches = row.reference
      ? row.reference.toLowerCase().includes(query)
      : false;

    return transactionIdMatches || userIdMatches || referenceMatches;
  });

  return (
    <>
      <Helmet>
        <title>Coins Transactions</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Coin Transactions
          </Typography>
        </Stack>

        <CoinTransactionsStatsChart
          stats={stats}
          loading={statsLoading}
          days={statsDays}
          onDaysChange={setStatsDays}
        />

        <TransactionsFilters
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
          transactionTypes={TRANSACTION_TYPES}
        />

        <Card>
          <CoinTransactionsTable
            transactions={filteredRows || []}
            lastTransactionRef={
              filteredRows.length >= limit ? lastTransactionRef : null
            }
          />

          {loading && rows?.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {rows?.length === 0 && loading && (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          )}
        </Card>
      </Container>
    </>
  );
}
